import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { can, visibleMenu, type Me, type MenuItem } from '../auth/me';
import { HelpMenu } from '../hints/HelpMenu';
import { HintLayer } from '../hints/Hint';
import { HintsProvider } from '../hints/HintsProvider';
import { Bell } from '../notifications/Bell';
import { forgetThisDevice, listenForPushClicks } from '../notifications/push';
import { useLinkedPerson } from '../schedule/MyTasksPage';
import { useAssignedTasks } from '../company-tasks/access';
import { useMyQuestionCount } from '../crew/useMyCalendar';
import { Brand } from '../ui/Brand';
import { ErrorBoundary } from '../ui/ErrorBoundary';
import { trapTab } from '../ui/focus';
import { MenuIcon } from './icons';
import { ADMIN, phoneMenu, sidebarMenu } from './menuOrder';
import { clearUndo } from '../events/undo';
import { NavSteps, useNavRecorder, useNavShortcuts } from '../nav/NavSteps';
import { useScrollRestore } from '../nav/urlState';
import { purgeNavStorage, setNavUser } from '../nav/session';
import { DialogLayer } from '../ui/Dialog';
import { NewVersionNotice, VersionBadge } from '../version/VersionBadge';
import { leaveApp, UnsavedChangesHost } from '../ui/unsaved';
import { ReservationConflictHost } from '../availability/conflicts';
import { CatalogOpenHost } from '../nav/CatalogOpenAsk';
import { useSelectNumbersOnFocus } from '../ui/selectNumbers';
import { ContractorColorsProvider } from '../contractors/colors';

function MenuLink({ item, extra = false }: { item: MenuItem; extra?: boolean }) {
  return (
    <NavLink to={item.to} className={({ isActive }) => [extra ? 'menu-extra' : '', isActive ? 'active' : ''].filter(Boolean).join(' ')} data-feature-id={item.featureId}>
      <MenuIcon to={item.to} />
      <span className="menu-label">{item.label}</span>
    </NavLink>
  );
}

/** Help, password and logout: at the bottom of the sidebar, and in the "Więcej" sheet on a phone. */
function AccountActions({ onLogout }: { onLogout: () => void }) {
  return (
    <div className="who-actions">
      <HelpMenu />
      <NavLink to="/haslo" className="btn ghost small" data-feature-id="identity.password.change">
        Zmień hasło
      </NavLink>
      <button type="button" className="btn ghost small" onClick={onLogout} data-feature-id="identity.logout">
        Wyloguj
      </button>
    </div>
  );
}

/**
 * Phone: the rarer sections and the account actions, in a sheet over the bottom bar. A modal: Tab stays
 * inside it, and when it closes focus goes back to what opened it ("Więcej").
 */
export function MoreSheet({ me, items, onLogout, onClose }: { me: Me; items: MenuItem[]; onLogout: () => void; onClose: () => void }) {
  const sheet = useRef<HTMLDivElement>(null);
  // Escape and the focus going back to "Więcej": `DialogLayer`; after following a link the opener may be gone, then
  // the new screen keeps the focus
  useEffect(() => {
    sheet.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (sheet.current) trapTab(sheet.current, e);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  const work = items.filter((i) => !ADMIN.has(i.to));
  const admin = items.filter((i) => ADMIN.has(i.to));
  return (
    <div className="sheet-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="sheet" role="dialog" aria-modal="true" aria-label="Więcej" tabIndex={-1} ref={sheet}>
        <div className="sheet-head">
          <span className="sheet-who" title={me.email}>
            {me.name}
          </span>
          <DialogLayer onClose={onClose} />
          <button type="button" className="btn ghost small" onClick={onClose}>
            Zamknij
          </button>
        </div>
        {work.length > 0 && (
          <nav className="sheet-links" aria-label="Pozostałe sekcje">
            {work.map((item) => (
              <MenuLink key={item.to} item={item} />
            ))}
          </nav>
        )}
        {admin.length > 0 && (
          <nav className="sheet-links" aria-label="Administracja">
            <span className="sheet-group">Administracja</span>
            {admin.map((item) => (
              <MenuLink key={item.to} item={item} />
            ))}
          </nav>
        )}
        <AccountActions onLogout={onLogout} />
      </div>
    </div>
  );
}

/**
 * App frame. Desktop: a light sidebar with the logo, the menu and the account actions at the bottom.
 * The bell (notifications) sits next to the logo on both.
 * Phone: a slim top bar with the logo, and a bottom tab bar with the four daily sections plus "Więcej"
 * (the other sections, help, password, logout). An account without any section has no menu at all;
 * on a phone its help, password and logout then stay in the top bar.
 */
export function AppLayout({ me }: { me: Me }) {
  const queryClient = useQueryClient();
  const assignedTasks = useAssignedTasks(me);
  const linkedPerson = useLinkedPerson(me);
  const myQuestions = useMyQuestionCount(true);
  const menu = visibleMenu(me, { assignedTasks, linkedPerson, myQuestions });
  const { work, admin } = sidebarMenu(menu);
  const { tabs, more } = phoneMenu(menu);
  const onPhoneTab = new Set(tabs.map((i) => i.to));
  const [moreOpen, setMoreOpen] = useState(false);
  const closeMore = useCallback(() => setMoreOpen(false), []);
  const location = useLocation();
  useEffect(() => setMoreOpen(false), [location.pathname]);
  // a click on a push notification while the app is open: go to its link here (the service worker asks)
  const navigate = useNavigate();
  useEffect(() => listenForPushClicks((link) => navigate(link)), [navigate]);
  // P13: "Wstecz / Dalej" between the app's screens; drafts, places and scroll are this account's only
  setNavUser(me.id);
  useEffect(() => purgeNavStorage(me.id), [me.id]);
  useNavRecorder();
  useNavShortcuts();
  useScrollRestore();
  // owner 2026-10-23 16:08: a click or Tab into a number field selects it, typing replaces it
  useSelectNumbersOnFocus();
  const moreActive = more.some((i) => location.pathname === i.to || location.pathname.startsWith(`${i.to}/`));

  async function logout() {
    try {
      // while the session still exists: this browser stops getting this account's push (never blocks logout)
      await forgetThisDevice();
      await api('/api/auth/logout', { method: 'POST' });
    } finally {
      queryClient.clear();
      clearUndo(); // the calendar's ⌘Z steps belong to this account
      purgeNavStorage(); // so do drafts, the list of places and scroll positions (P13)
      leaveApp(() => window.location.assign('/login'));
    }
  }
  // on a phone, sections outside the four daily ones are hidden from the bar and reached through "Więcej"
  const extra = (item: MenuItem) => !onPhoneTab.has(item.to);

  return (
    <HintsProvider>
      {/* owner 2026-10-23 20:49: contractor colours for the calendar, quotes and packlists (only for who sees contractors) */}
      <ContractorColorsProvider enabled={can(me, 'contractors.contractors', 'view')}>
      <div className={`shell${menu.length > 0 ? ' has-menu' : ''}`}>
        <header className="sidebar">
          <div className="sidebar-top">
            <span className="brand-with-version">
              <NavLink to="/" className="sidebar-brand" aria-label="Mek Records – strona główna">
                <Brand />
              </NavLink>
              {/* „v102”: which version runs, so the owner knows an update has arrived */}
              <VersionBadge history />
            </span>
            {/* phone: in the top bar; a computer has them above the screen (the sidebar stays as it is) */}
            <NavSteps className="nav-steps-phone" />
            <Bell />
          </div>
          {menu.length > 0 && (
            <nav className="menu" aria-label="Menu główne">
              {work.map((item) => (
                <MenuLink key={item.to} item={item} extra={extra(item)} />
              ))}
              {admin.length > 0 && (
                <>
                  <span className="menu-group" aria-hidden="true">
                    Administracja
                  </span>
                  {admin.map((item) => (
                    <MenuLink key={item.to} item={item} extra={extra(item)} />
                  ))}
                </>
              )}
              <button type="button" className={`menu-more${moreActive ? ' active' : ''}`} aria-haspopup="dialog" aria-expanded={moreOpen} onClick={() => setMoreOpen(true)}>
                <MenuIcon to="more" />
                <span className="menu-label">Więcej</span>
              </button>
            </nav>
          )}
          <div className="who">
            <span className="who-name" title={me.email}>
              {me.name}
            </span>
            <AccountActions onLogout={logout} />
          </div>
        </header>
        <main className="content">
          <div className="content-bar">
            <NavSteps />
          </div>
          <NewVersionNotice />
          {/* owner 2026-10-23 13:34: „Masz niezapisane zmiany” before closing a changed form */}
          <UnsavedChangesHost />
          {/* owner 2026-10-24: „Dodaj mimo to” before gear another event reserved first is added */}
          <ReservationConflictHost />
          {/* owner 2026-10-24 10:53: Ctrl+klik on a line asks before opening it in the catalog */}
          <CatalogOpenHost />
          {/* a broken screen keeps the menu; going to another section clears the error */}
          <ErrorBoundary key={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </main>
        {moreOpen && <MoreSheet me={me} items={more} onLogout={logout} onClose={closeMore} />}
      </div>
      </ContractorColorsProvider>
      <HintLayer />
    </HintsProvider>
  );
}
